import { useState } from 'react'
import { usePrimaryPage } from '@/PageManager'
import { simplifyUrl } from '@/lib/url'
import { unwrappedFeedAtom } from '@/atoms/feed'
import { switchFeed, trackRecentRelayFeed } from '@/PageManager'
import { useFetchRelayInfo } from '@/hooks/useFetchRelayInfo'
import RelayIcon from '@/components/RelayIcon'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  Folder01Icon,
  ServerStack01Icon
} from '@hugeicons/core-free-icons'
import SidebarItem from './SidebarItem'
import { useAtomValue } from 'jotai'
import { hideFavoriteRelaysAtom } from '@/atoms/preferences'
import { unwrappedFavoriteRelaysWithSetsAtom } from '@/atoms/lists'
import { ResolvedSet } from '@nostr/gadgets/sets'

export default function RelayFeedButton({ collapse }: { collapse: boolean }) {
  const { navigate, current, display } = usePrimaryPage()
  const feed = useAtomValue(unwrappedFeedAtom)
  const favorites = useAtomValue(unwrappedFavoriteRelaysWithSetsAtom)
  const hideFavoriteRelays = useAtomValue(hideFavoriteRelaysAtom)

  const [showAll, setShowAll] = useState(false)

  if (hideFavoriteRelays || !favorites?.length) return null

  const recentOrder = readRecentOrder()
  const sortedFavorites = [...favorites].sort((a, b) => {
    const idA = getItemId(a)
    const idB = getItemId(b)
    const idxA = recentOrder.indexOf(idA)
    const idxB = recentOrder.indexOf(idB)
    if (idxA === -1 && idxB === -1) return 0
    if (idxA === -1) return 1
    if (idxB === -1) return -1
    return idxA - idxB
  })

  const mainItem = sortedFavorites[0]
  const otherItems = sortedFavorites.slice(1)
  const displayedItems = showAll ? otherItems : otherItems.slice(0, 3)
  const hasMore = otherItems.length > 3

  return (
    <div>
      {typeof mainItem === 'string' ? (
        <SidebarItem
          title={simplifyUrl(mainItem)}
          onClick={() => {
            navigate('home')
            switchFeed({ relay: mainItem })
            trackRecentRelayFeed(mainItem)
          }}
          active={display && current === 'home' && feed && 'relay' in feed}
          collapse={collapse}
        >
          <HugeiconsIcon icon={ServerStack01Icon} />
        </SidebarItem>
      ) : (
        <SidebarItem
          title={`${mainItem.title} (${mainItem.items.length})`}
          onClick={() => {
            navigate('home')
            switchFeed({
              relaySet: mainItem
            })
            trackRecentRelayFeed(mainItem)
          }}
          active={
            display &&
            current === 'home' &&
            feed &&
            'relaySet' in feed &&
            feed.relaySet.pointer.identifier === mainItem.pointer.identifier &&
            feed.relaySet.pointer.pubkey === mainItem.pointer.pubkey
          }
          collapse={collapse}
        >
          <HugeiconsIcon icon={Folder01Icon} />
        </SidebarItem>
      )}
      {!collapse && otherItems.length > 0 && (
        <div className="flex flex-col items-end gap-px mt-px pr-1">
          {displayedItems.map((item) =>
            typeof item === 'string' ? (
              <RelayFeedSubItem key={item} url={item} onClick={() => setShowAll(false)} />
            ) : (
              <RelaySetSubItem
                key={item.pointer.pubkey + item.pointer.identifier}
                set={item}
                onClick={() => setShowAll(false)}
              />
            )
          )}
          {hasMore && (
            <button
              onClick={() => setShowAll(!showAll)}
              className="flex items-center justify-center w-full text-muted-foreground"
            >
              {showAll ? (
                <HugeiconsIcon icon={ChevronUpIcon} className="size-3" />
              ) : (
                <HugeiconsIcon icon={ChevronDownIcon} className="size-3" />
              )}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function getItemId(item: string | ResolvedSet<string>): string {
  if (typeof item === 'string') return item
  return `set:${item.pointer.pubkey}:${item.pointer.identifier}`
}

function readRecentOrder(): string[] {
  try {
    const stored = localStorage.getItem('s:recentRelayFeeds')
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function RelayFeedSubItem({ url, onClick }: { url: string; onClick: () => void }) {
  const { navigate } = usePrimaryPage()
  const { relayInfo } = useFetchRelayInfo(url)

  return (
    <button
      onClick={() => {
        onClick()
        navigate('home')
        switchFeed({ relay: url })
        trackRecentRelayFeed(url)
      }}
      className="flex items-center gap-1.5 w-5/6 px-2 py-1 rounded text-xs text-muted-foreground hover:bg-accent/40 transition-colors truncate"
    >
      <RelayIcon url={url} iconSize={10} className="w-4 h-4" />
      <span className="truncate">{relayInfo?.urlSimple || url}</span>
    </button>
  )
}

function RelaySetSubItem({ set, onClick }: { set: ResolvedSet<string>; onClick: () => void }) {
  const { navigate } = usePrimaryPage()

  return (
    <button
      onClick={() => {
        onClick()
        navigate('home')
        switchFeed({ relaySet: set })
        trackRecentRelayFeed(set)
      }}
      className="flex items-center gap-1.5 w-5/6 px-2 py-1 rounded text-xs text-muted-foreground hover:bg-accent/40 transition-colors truncate"
    >
      <HugeiconsIcon icon={Folder01Icon} className="w-4 h-4" />
      <span className="truncate">
        {set.title} ({set.items.length})
      </span>
    </button>
  )
}
